const FLOW_STORAGE_PREFIX = "madamuh";
const FLOW_COUNTER_TTL_MS = 30 * 60 * 1000;

export type FlowStorageNamespace =
  | "normalEntry"
  | "ownerOnboarding"
  | "memberInvite"
  | "studentSelfInvite"
  | "studentInvite"
  | "guardianInvite"
  | "debugLineSub";

type FlowCounterKey =
  | "loginAttemptCount"
  | "backendRecoveryAttemptCount"
  | "expiredTokenReauthAttemptCount";
type FlowStringKey =
  | "token"
  | "tenantSlug"
  | "returnTo"
  | "manualCode"
  | "loginStartedAt"
  | "forceReauthStartedAt";

function getFlowStorageNamespaces(namespace: FlowStorageNamespace): string[] {
  if (namespace === "studentSelfInvite" || namespace === "studentInvite") {
    return ["studentSelfInvite", "studentInvite"];
  }

  return [namespace];
}

function getPrimaryFlowStorageNamespace(namespace: FlowStorageNamespace): string {
  return namespace === "studentInvite" ? "studentSelfInvite" : namespace;
}

function getFlowStorageKey(namespace: FlowStorageNamespace, key: FlowCounterKey | FlowStringKey): string {
  return `${FLOW_STORAGE_PREFIX}:${getPrimaryFlowStorageNamespace(namespace)}:${key}`;
}

function getFlowStorageKeys(namespace: FlowStorageNamespace, key: FlowCounterKey | FlowStringKey): string[] {
  return getFlowStorageNamespaces(namespace).map((candidateNamespace) => `${FLOW_STORAGE_PREFIX}:${candidateNamespace}:${key}`);
}

function parseCounterRecord(rawValue: string | null): { count: number; updatedAt: number } | null {
  if (!rawValue) {
    return null;
  }

  const [countRaw, updatedAtRaw] = rawValue.split("|");
  const count = Number.parseInt(countRaw ?? "", 10);
  const updatedAt = Number.parseInt(updatedAtRaw ?? "", 10);

  if (!Number.isFinite(count) || !Number.isFinite(updatedAt)) {
    return null;
  }

  return {
    count,
    updatedAt
  };
}

function readFlowCounter(namespace: FlowStorageNamespace, key: FlowCounterKey): number {
  for (const storageKey of getFlowStorageKeys(namespace, key)) {
    const parsedRecord = parseCounterRecord(window.sessionStorage.getItem(storageKey));

    if (!parsedRecord) {
      window.sessionStorage.removeItem(storageKey);
      continue;
    }

    if (Date.now() - parsedRecord.updatedAt > FLOW_COUNTER_TTL_MS) {
      window.sessionStorage.removeItem(storageKey);
      continue;
    }

    return parsedRecord.count;
  }

  return 0;
}

function setFlowCounter(namespace: FlowStorageNamespace, key: FlowCounterKey, count: number): void {
  window.sessionStorage.setItem(getFlowStorageKey(namespace, key), `${count}|${Date.now()}`);
}

export function incrementFlowCounter(namespace: FlowStorageNamespace, key: FlowCounterKey): number {
  const nextCount = readFlowCounter(namespace, key) + 1;
  setFlowCounter(namespace, key, nextCount);
  return nextCount;
}

export function clearFlowCounter(namespace: FlowStorageNamespace, key: FlowCounterKey): void {
  for (const storageKey of getFlowStorageKeys(namespace, key)) {
    window.sessionStorage.removeItem(storageKey);
  }
}

export function clearFlowCounters(namespace: FlowStorageNamespace): void {
  clearFlowCounter(namespace, "loginAttemptCount");
  clearFlowCounter(namespace, "backendRecoveryAttemptCount");
  clearFlowCounter(namespace, "expiredTokenReauthAttemptCount");
}

export function getFlowCounter(namespace: FlowStorageNamespace, key: FlowCounterKey): number {
  return readFlowCounter(namespace, key);
}

export function readFlowString(namespace: FlowStorageNamespace, key: FlowStringKey): string | null {
  for (const storageKey of getFlowStorageKeys(namespace, key)) {
    const value = window.sessionStorage.getItem(storageKey)?.trim() ?? "";

    if (value.length > 0) {
      return value;
    }
  }

  return null;
}

export function writeFlowString(namespace: FlowStorageNamespace, key: FlowStringKey, value: string): void {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return;
  }

  window.sessionStorage.setItem(getFlowStorageKey(namespace, key), normalizedValue);
}

export function clearFlowString(namespace: FlowStorageNamespace, key: FlowStringKey): void {
  for (const storageKey of getFlowStorageKeys(namespace, key)) {
    window.sessionStorage.removeItem(storageKey);
  }
}

export function clearFlowTransientState(
  namespace: FlowStorageNamespace,
  options?: {
    preserveToken?: boolean;
    preserveManualCode?: boolean;
  }
): void {
  clearFlowCounters(namespace);
  clearFlowString(namespace, "tenantSlug");
  clearFlowString(namespace, "returnTo");
  clearFlowString(namespace, "loginStartedAt");
  clearFlowString(namespace, "forceReauthStartedAt");

  if (!options?.preserveToken) {
    clearFlowString(namespace, "token");
  }

  if (!options?.preserveManualCode) {
    clearFlowString(namespace, "manualCode");
  }
}

export function readFlowToken(namespace: FlowStorageNamespace, tenantSlug?: string): string | null {
  if (tenantSlug) {
    const storedTenantSlug = readFlowString(namespace, "tenantSlug");

    if (storedTenantSlug && storedTenantSlug !== tenantSlug) {
      return null;
    }
  }

  return readFlowString(namespace, "token");
}

export function rememberFlowToken(namespace: FlowStorageNamespace, token: string): void {
  writeFlowString(namespace, "token", token);
}

export function rememberFlowTenantSlug(namespace: FlowStorageNamespace, tenantSlug: string): void {
  writeFlowString(namespace, "tenantSlug", tenantSlug);
}

export function readFlowReturnTo(namespace: FlowStorageNamespace): string | null {
  return readFlowString(namespace, "returnTo");
}

export function rememberFlowReturnTo(namespace: FlowStorageNamespace, returnTo: string): void {
  writeFlowString(namespace, "returnTo", returnTo);
}

export function readFlowLoginStartedAt(namespace: FlowStorageNamespace): number | null {
  const rawValue = readFlowString(namespace, "loginStartedAt");

  if (!rawValue) {
    return null;
  }

  const timestamp = Number.parseInt(rawValue, 10);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function rememberFlowLoginStartedAt(namespace: FlowStorageNamespace, timestamp: number): void {
  writeFlowString(namespace, "loginStartedAt", String(timestamp));
}

export function clearFlowLoginStartedAt(namespace: FlowStorageNamespace): void {
  clearFlowString(namespace, "loginStartedAt");
}

export function readFlowForceReauthStartedAt(namespace: FlowStorageNamespace): string | null {
  return readFlowString(namespace, "forceReauthStartedAt");
}

export function rememberFlowForceReauthStartedAt(namespace: FlowStorageNamespace, timestamp: string): void {
  writeFlowString(namespace, "forceReauthStartedAt", timestamp);
}

export function clearFlowForceReauthStartedAt(namespace: FlowStorageNamespace): void {
  clearFlowString(namespace, "forceReauthStartedAt");
}
